<template>
    <v-dialog :value="value" max-width="1040" @input="onInput">
        <v-card class="doc-viewer">
            <div class="doc-viewer__bar">
                <v-icon color="primary">{{ icon }}</v-icon>
                <div class="doc-viewer__name text-truncate" :title="displayName">{{ displayName }}</div>
                <v-spacer></v-spacer>
                <v-btn small text :disabled="!objectUrl" @click="download"><v-icon left small>mdi-download</v-icon>Baixar</v-btn>
                <v-btn small text :disabled="!objectUrl" class="d-none d-sm-flex" @click="openInTab"><v-icon left small>mdi-open-in-new</v-icon>Nova aba</v-btn>
                <v-btn icon small aria-label="Fechar" @click="close"><v-icon>mdi-close</v-icon></v-btn>
            </div>

            <div class="doc-viewer__body">
                <div v-if="loading" class="doc-viewer__state">
                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    <div class="mt-3">Carregando o documento...</div>
                </div>
                <div v-else-if="error" class="doc-viewer__state">
                    <v-icon large color="error">mdi-alert-circle-outline</v-icon>
                    <div class="mt-2">{{ error }}</div>
                    <v-btn small outlined class="mt-3" @click="load">Tentar de novo</v-btn>
                </div>
                <iframe v-else-if="kind === 'pdf'" :src="objectUrl" class="doc-viewer__frame" title="Documento"></iframe>
                <div v-else-if="kind === 'image'" class="doc-viewer__image">
                    <img :src="objectUrl" :alt="displayName" />
                </div>
                <div v-else-if="objectUrl" class="doc-viewer__state">
                    <v-icon large>mdi-file-document-outline</v-icon>
                    <div class="mt-2">Este formato não tem pré-visualização. Baixe o arquivo para abrir.</div>
                    <v-btn color="primary" depressed class="mt-3" @click="download"><v-icon left small>mdi-download</v-icon>Baixar documento</v-btn>
                </div>
            </div>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from "axios";

const EXTENSIONS = { "application/pdf": "pdf", "image/png": "png", "image/jpeg": "jpg", "image/gif": "gif", "image/webp": "webp" };

// Visualizador de documentos dentro do painel: o arquivo vem da API na hora em que
// a janela abre (sem link do storage, que vencia com a tela aberta). PDF e imagem
// aparecem aqui mesmo; outros formatos, para baixar.
export default {
    name: "DocumentViewerDialog",

    props: {
        value: { type: Boolean, default: false },
        // Rota da API que entrega o arquivo (ex.: /api/client/files/user-docs/12).
        source: { type: String, default: null },
        fileName: { type: String, default: "Documento" },
    },

    data: () => ({ loading: false, error: null, objectUrl: null, contentType: "" }),

    computed: {
        kind() {
            if (this.contentType === "application/pdf") return "pdf";
            if (/^image\//.test(this.contentType)) return "image";
            return "other";
        },
        icon() {
            if (this.kind === "pdf") return "mdi-file-pdf-box";
            if (this.kind === "image") return "mdi-file-image-outline";
            return "mdi-file-document-outline";
        },
        displayName() {
            const name = String(this.fileName || "Documento").trim() || "Documento";
            const extension = EXTENSIONS[this.contentType];
            return extension && !/\.[a-z0-9]{2,5}$/i.test(name) ? `${name}.${extension}` : name;
        },
    },

    watch: {
        value(open) {
            if (open) this.load();
            else this.release();
        },
        source() {
            if (this.value) this.load();
        },
    },

    beforeDestroy() {
        this.release();
    },

    methods: {
        async load() {
            if (!this.source) return;
            this.release();
            this.loading = true;
            this.error = null;
            try {
                const result = await axios.get(this.source, { responseType: "blob" });
                this.contentType = String(result.data.type || "").toLowerCase();
                this.objectUrl = window.URL.createObjectURL(result.data);
            } catch (error) {
                this.error = await this.errorText(error);
            } finally {
                this.loading = false;
            }
        },

        // Erro da API chega como Blob quando responseType = blob.
        async errorText(error) {
            const data = error && error.response && error.response.data;
            if (data instanceof Blob) {
                try {
                    const parsed = JSON.parse(await data.text());
                    const message = parsed && parsed.content && parsed.content.error && parsed.content.error.message;
                    if (message) return message;
                } catch (parseError) {
                    // segue para a mensagem padrao
                }
            }
            return "Não foi possível abrir o documento. Tente novamente.";
        },

        release() {
            if (this.objectUrl) window.URL.revokeObjectURL(this.objectUrl);
            this.objectUrl = null;
            this.contentType = "";
        },

        download() {
            if (!this.objectUrl) return;
            const link = document.createElement("a");
            link.href = this.objectUrl;
            link.download = this.displayName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },

        openInTab() {
            if (this.objectUrl) window.open(this.objectUrl, "_blank");
        },

        onInput(open) {
            if (!open) this.close();
        },

        close() {
            this.$emit("input", false);
        },
    },
};
</script>

<style scoped>
.doc-viewer {
    display: flex;
    flex-direction: column;
    height: 86vh;
    overflow: hidden;
}

.doc-viewer__bar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-bottom: 1px solid #e5e7eb;
    flex: none;
}

.doc-viewer__name {
    font-weight: 600;
    min-width: 0;
}

.doc-viewer__body {
    flex: 1 1 auto;
    min-height: 0;
    background: #f4f4f7;
    display: flex;
}

.doc-viewer__frame {
    flex: 1;
    width: 100%;
    height: 100%;
    border: 0;
    background: #fff;
}

.doc-viewer__image {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    overflow: auto;
}

.doc-viewer__image img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
    background: #fff;
}

.doc-viewer__state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px;
    text-align: center;
    color: #555;
}
</style>
